import React from 'react';
import { useAuth } from '../context/AuthContext';
import { AppFeedbackSection } from '../components/user/AppFeedbackSection';
import { userService } from '../services/userService';
import { UserProfile as UserProfileType } from '../types/user';
import { InlineEditField } from '../components/ui/InlineEditField';
import { InlineEditSelect } from '../components/ui/InlineEditSelect';
import { InlineEditToggle } from '../components/ui/InlineEditToggle';
import { DEFAULT_URLS } from '../config/defaults';

const UserProfile: React.FC = () => {
  const { user, signOut, refreshUser } = useAuth();

  // Field update handlers for inline editing
  const handleFieldUpdate = async <T extends keyof UserProfileType>(
    field: T,
    value: UserProfileType[T]
  ) => {
    if (!user) throw new Error('User not authenticated');
    
    await userService.updateField(field, value);
    await refreshUser();
  };

  const handleDisplayNameUpdate = (value: string) => handleFieldUpdate('displayName', value);
  const handleEmailUpdate = (value: string) => handleFieldUpdate('email', value);
  const handleLanguageUpdate = (value: string) => handleFieldUpdate('language', value);
  const handleTimezoneUpdate = (value: string) => handleFieldUpdate('timezone', value);
  
  const handleDataProcessingConsentUpdate = async (consented: boolean) => {
    if (!user) throw new Error('User not authenticated');
    
    const updates: Partial<UserProfileType> = {
      metadata: {
        ...user.metadata,
        dataProcessingConsent: consented
      }
    };
    
    await userService.updateProfile(updates);
    await refreshUser();
  };

  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  return (
    <div className="h-full overflow-y-auto bg-rm-bg-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Profile Panel */}
          <div className="space-y-6">
            <div className="bg-white shadow-card rounded-card p-6 border border-rm-neutral-600/20">
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-rm-primary-700">Profile</h1>

              </div>
            
            {user && (
              <div className="space-y-6">
                <InlineEditField
                  value={user.displayName}
                  onSave={handleDisplayNameUpdate}
                  label="Display Name"
                  placeholder="Enter display name"
                  type="text"
                  required
                  maxLength={100}
                  validation={(value) => {
                    if (value.trim().length === 0) return 'Display name is required';
                    if (value.trim().length > 100) return 'Display name cannot exceed 100 characters';
                    return null;
                  }}
                />
                
                <InlineEditField
                  value={user.email}
                  onSave={handleEmailUpdate}
                  label="Email"
                  placeholder="Enter email address"
                  type="email"
                  required
                  maxLength={255}
                  validation={(value) => {
                    if (!value.includes('@')) return 'Invalid email address';
                    if (value.length > 255) return 'Email cannot exceed 255 characters';
                    return null;
                  }}
                />
                
                <InlineEditSelect
                  value={user.language}
                  onSave={handleLanguageUpdate}
                  label="Language"
                  options={[
                    { value: 'en', label: 'English' },
                    { value: 'nl', label: 'Dutch' }
                  ]}
                  required
                />

                <InlineEditSelect
                  value={user.timezone || 'Europe/Amsterdam'}
                  onSave={handleTimezoneUpdate}
                  label="Timezone"
                  options={[
                    { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET)' },
                    { value: 'UTC', label: 'UTC' },
                    { value: 'Europe/London', label: 'Europe/London (GMT)' },
                    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET)' },
                    { value: 'Europe/Paris', label: 'Europe/Paris (CET)' },
                    { value: 'America/New_York', label: 'America/New_York (EST)' },
                    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST)' },
                    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
                    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEDT)' }
                  ]}
                  required
                />
                
                {/* Roles Display */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-rm-ink-900">
                    Roles
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {user.roles.map((role) => (
                      <span
                        key={role}
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          role === 'admin' 
                            ? 'bg-red-100 text-red-800'
                            : role === 'reportsReader'
                            ? 'bg-rm-accent-300 text-rm-ink-900'
                            : role === 'moderator'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-rm-bg-tint text-rm-ink-900'
                        }`}
                      >
                        {role === 'reportsReader' ? 'Reports Reader' : 
                         role.charAt(0).toUpperCase() + role.slice(1)}
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-rm-neutral-600">
                    Your account roles determine which features and sections you can access.
                  </p>
                </div>
                
                {/* Data Processing Consent - only show if consent configuration exists */}
                {user.consent && (
                  <InlineEditToggle
                    value={user.metadata.dataProcessingConsent || false}
                    onSave={handleDataProcessingConsentUpdate}
                    label="Data Processing Consent"
                    trueLabel="Consented"
                    falseLabel="Withdraw Consent"
                    displayAs="badge"
                  />
                )}
                
                {/* Privacy Notice Link - always show */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-rm-ink-900">
                    Privacy Notice
                  </label>
                  <a
                    href={user.consent?.privacyUrl || DEFAULT_URLS.PRIVACY_POLICY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm text-rm-accent-600 hover:text-rm-accent-700 underline font-medium transition-colors"
                  >
                    View Privacy Notice
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <p className="text-xs text-rm-neutral-600">
                    Read our privacy notice to understand how we process your data.
                  </p>
                </div>
                
                {/* User ID */}
                <div className="text-xs text-rm-neutral-600">
                  <span className="font-medium">User ID:</span>{' '}
                  <code className="font-mono">{user.id.includes('@') ? user.id.split('@')[0] : user.id.substring(0, 8)}</code>
                </div>
              </div>
            )}

            {/* Sign Out */}
            <div className="mt-6 pt-6 border-t border-rm-neutral-600/20">
              <button
                onClick={handleSignOut}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-card text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors shadow-soft"
              >
                Sign Out
              </button>
            </div>
            </div>
          </div>
          
          {/* Right Column */}
          <div className="space-y-6">
            {/* App Feedback Panel */}
            <div className="h-fit">
              <AppFeedbackSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
